import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Icon to display before text */
  leftIcon?: ReactNode;
  /** Icon to display after text */
  rightIcon?: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Children content */
  children?: ReactNode;
}

/**
 * Button component with CGIC brand styling
 *
 * Supports multiple variants (primary, secondary, danger, ghost, success)
 * and sizes (sm, md, lg). Includes loading states and icon support.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    // Base classes for all buttons
    const baseClasses = [
      'inline-flex items-center justify-center gap-2',
      'font-body font-medium',
      'rounded-lg',
      'transition-all duration-200 ease-smooth',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      // Minimum touch target size for accessibility
      'min-h-touch min-w-touch',
    ];

    // Size variants
    const sizeClasses: Record<ButtonSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2.5 text-sm',
      lg: 'px-6 py-3 text-base',
    };

    // Variant styles
    const variantClasses: Record<ButtonVariant, string> = {
      primary: [
        'bg-primary-600 text-white',
        'hover:bg-primary-700 active:bg-primary-800',
        'focus-visible:ring-primary-500',
        'shadow-sm hover:shadow-primary',
      ].join(' '),
      secondary: [
        'bg-neutral-100 text-neutral-700 border border-neutral-300',
        'hover:bg-neutral-200 active:bg-neutral-300',
        'focus-visible:ring-neutral-400',
        'dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700',
        'dark:hover:bg-neutral-700 dark:active:bg-neutral-600',
      ].join(' '),
      danger: [
        'bg-danger-500 text-white',
        'hover:bg-danger-600 active:bg-danger-700',
        'focus-visible:ring-danger-500',
        'shadow-sm hover:shadow-danger',
      ].join(' '),
      ghost: [
        'bg-transparent text-neutral-600',
        'hover:bg-neutral-100 active:bg-neutral-200',
        'focus-visible:ring-neutral-400',
        'dark:text-neutral-300 dark:hover:bg-neutral-800 dark:active:bg-neutral-700',
      ].join(' '),
      success: [
        'bg-success-600 text-white',  // Using 600 for WCAG AA contrast (4.5:1)
        'hover:bg-success-700 active:bg-success-800',
        'focus-visible:ring-success-500',
        'shadow-sm hover:shadow-success',
      ].join(' '),
    };

    const widthClass = fullWidth ? 'w-full' : '';

    const combinedClassName = [
      ...baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      widthClass,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={combinedClassName}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner size={size} />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

/** Internal loading spinner component */
function LoadingSpinner({ size }: { size: ButtonSize }) {
  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default Button;
